"use client";

import { useState, useRef, useEffect, useCallback, useMemo, memo, MouseEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./ImageTrailHero.module.css";
import { useTranslations } from "next-intl";
import { ShowcaseImage } from "@/app/interfaces/showcaseImage";
import { getTrailImageUrl, TRAIL_IMAGE_WIDTH, TRAIL_IMAGE_HEIGHT } from "./trailImageUrl";

const MAX_ITEMS = 15;
const MOUSE_THROTTLE_MS = 50;
const DIST_THRESHOLD = 150;
const IDLE_TIMEOUT_MS = 3000;
const AMBIENT_INTERVAL_MS = 1500;
const PURGE_INTERVAL_MS = 500;

interface TrailItem {
  id: number;
  x: number;
  y: number;
  imageUrl: string;
  rotation: number;
  createdAt: number;
  type: "trail" | "ambient";
}

const SPRING_TRANSITION = { type: "spring", stiffness: 400, damping: 28, mass: 0.8 } as const;

// Items are immutable, so memo keeps spawn/purge state updates from re-rendering
// every image already on screen.
const TrailImage = memo(function TrailImage({ item }: { item: TrailItem }) {
  return (
    <motion.img
      src={item.imageUrl}
      alt=""
      width={TRAIL_IMAGE_WIDTH}
      height={TRAIL_IMAGE_HEIGHT}
      decoding="async"
      draggable={false}
      className={styles.trailImage}
      style={{ left: item.x, top: item.y }}
      initial={{ opacity: 0, scale: 0.2, x: "-50%", y: "-50%", rotate: item.rotation }}
      animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%", rotate: item.rotation }}
      exit={{
        opacity: 0,
        scale: 0.8,
        y: item.type === "ambient" ? "-60%" : "-50%",
        transition: { duration: item.type === "ambient" ? 2 : 1 },
      }}
      transition={SPRING_TRANSITION}
    />
  );
});

export default function ImageTrailHero({ images }: { images: ShowcaseImage[] }) {
  const t = useTranslations("MediaPage");

  const trailUrls = useMemo(
    () => (images.length > 0 ? images.map(getTrailImageUrl) : ["/placeholder.jpg"]),
    [images]
  );

  const [items, setItems] = useState<TrailItem[]>([]);

  const itemsRef = useRef<TrailItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const imageIndexRef = useRef(0);
  const ambientIndexRef = useRef(-1);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number>(0);
  const nextIdRef = useRef(0);

  const isIdleRef = useRef(true);
  const lastMouseMoveRef = useRef(0);
  const lastPurgeRef = useRef(0);
  const lastAmbientRef = useRef(0);
  const sideToggleRef = useRef(0);
  const trailUrlsRef = useRef(trailUrls);
  const isDesktopRef = useRef(false);
  const reducedMotionRef = useRef(false);

  useEffect(() => { trailUrlsRef.current = trailUrls; }, [trailUrls]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => { rectRef.current = el.getBoundingClientRect(); };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const desktopMq = window.matchMedia("(min-width: 1025px)");
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const update = () => {
      isDesktopRef.current = desktopMq.matches;
      reducedMotionRef.current = motionMq.matches;
    };
    update();

    desktopMq.addEventListener("change", update);
    motionMq.addEventListener("change", update);
    return () => {
      desktopMq.removeEventListener("change", update);
      motionMq.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const tick = () => {
      const now = Date.now();
      let didChange = false;

      if (now - lastPurgeRef.current > PURGE_INTERVAL_MS) {
        lastPurgeRef.current = now;
        const before = itemsRef.current.length;
        itemsRef.current = itemsRef.current.filter((item) => {
          const lifespan = item.type === "ambient" ? 3000 : 1500;
          return now - item.createdAt < lifespan;
        });
        if (itemsRef.current.length !== before) didChange = true;
      }

      if (isIdleRef.current && !reducedMotionRef.current && rectRef.current) {
        const isFirstAmbientBurst = lastAmbientRef.current === 0;
        const count = isFirstAmbientBurst ? 2 : 1;

        if (now - lastAmbientRef.current > AMBIENT_INTERVAL_MS) {
          lastAmbientRef.current = now;
          const rect = rectRef.current;
          const urls = trailUrlsRef.current;

          for (let i = 0; i < count; i++) {
            if (itemsRef.current.length >= MAX_ITEMS) break;

            const side = isFirstAmbientBurst ? i : sideToggleRef.current;

            const xMin = side === 0 ? 0.05 : 0.65;
            const xMax = side === 0 ? 0.35 : 0.95;

            const x = rect.width * (xMin + Math.random() * (xMax - xMin));
            const y = rect.height * (0.15 + Math.random() * 0.6);

            let idx: number;
            do { idx = Math.floor(Math.random() * urls.length); }
            while (urls.length > 1 && idx === ambientIndexRef.current);
            ambientIndexRef.current = idx;

            itemsRef.current.push({
              id: nextIdRef.current++,
              x, y,
              imageUrl: urls[idx],
              rotation: Math.random() * 30 - 15,
              createdAt: now,
              type: "ambient",
            });

            sideToggleRef.current = side === 0 ? 1 : 0;
          }
          didChange = true;
        }
      }

      if (didChange) setItems([...itemsRef.current]);
      rafRef.current = requestAnimationFrame(tick);
    };

    // Only run the loop while the hero is actually on screen.
    const io = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(rafRef.current);
      if (entry.isIntersecting) {
        rafRef.current = requestAnimationFrame(tick);
      }
    });
    io.observe(el);

    return () => {
      io.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const spawnItem = useCallback((x: number, y: number, type: "trail" | "ambient") => {
    if (itemsRef.current.length >= MAX_ITEMS) {
      itemsRef.current.shift();
    }

    const urls = trailUrlsRef.current;
    const idx = imageIndexRef.current % urls.length;
    imageIndexRef.current = (imageIndexRef.current + 1) % urls.length;

    itemsRef.current.push({
      id: nextIdRef.current++,
      x, y,
      imageUrl: urls[idx],
      rotation: Math.random() * 40 - 20,
      createdAt: Date.now(),
      type,
    });

    setItems([...itemsRef.current]);
  }, []);

  const wakeUp = useCallback(() => {
    isIdleRef.current = false;

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      lastAmbientRef.current = 0;
    }, IDLE_TIMEOUT_MS);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const now = Date.now();
    if (now - lastMouseMoveRef.current < MOUSE_THROTTLE_MS) return;
    lastMouseMoveRef.current = now;

    if (!isDesktopRef.current || reducedMotionRef.current || !rectRef.current) return;

    wakeUp();

    const x = e.clientX - rectRef.current.left;
    const y = e.clientY - rectRef.current.top;

    const dx = x - lastPosition.current.x;
    const dy = y - lastPosition.current.y;

    if (dx * dx + dy * dy > DIST_THRESHOLD * DIST_THRESHOLD) {
      lastPosition.current = { x, y };
      spawnItem(x, y, "trail");
    }
  }, [wakeUp, spawnItem]);

  const handleTap = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!rectRef.current || reducedMotionRef.current) return;
    wakeUp();
    const x = e.clientX - rectRef.current.left;
    const y = e.clientY - rectRef.current.top;
    spawnItem(x, y, "trail");
  }, [wakeUp, spawnItem]);

  return (
    <section
      className={styles.heroContainer}
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onPointerDown={handleTap}
    >
      <div className={styles.textContent}>
        <motion.h1
          className={styles.title}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {t("eventsGallery")} & {t("publications")}
        </motion.h1>
      </div>

      <AnimatePresence>
        {items.map((item) => (
          <TrailImage key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </section>
  );
}
